import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, Globe, ShieldCheck, Zap } from "lucide-react";

// Assets
import backgroundImage from "../assets/Home/home.png";
import projectManagementImage from "../assets/Home/projectManagementImage.avif";
import civilEngineeringImage from "../assets/Home/civilEngineeringImage.png";
import projectrecvoyImage from "../assets/Home/projectrecvoyImage.webp";
import contractManagementImage from "../assets/Home/contractManagementImage.jpg";
import communicationManagementImage from "../assets/Home/communicationManagementImage.webp";
import projecthumanResourcesImage from "../assets/Home/projecthumanResourcesImage.jpg";
import valueEngineeringImage from "../assets/Home/valueEngineeringImage.jpeg";
import hydrologyImage from "../assets/Home/hydrologyImage.png";
import onabortionalHealthSafetyImage from "../assets/Home/onabortionalHealthSafetyImage.png";
import roadsAndBridgesImage from "../assets/Home/roadsAndBridgesImage.jpg";

const services = [
  { title: "Project Planning", description: "Strategic planning to ensure efficient and timely project execution.", image: projectManagementImage, icon: <Zap className="text-accent" /> },
  { title: "Civil/Structural Design", description: "Comprehensive design services for civil and structural projects.", image: civilEngineeringImage, icon: <CheckCircle2 className="text-accent" /> },
  { title: "Project Recovery", description: "Specialized planning to bring delayed or failing projects back on track.", image: projectrecvoyImage, icon: <ShieldCheck className="text-accent" /> },
  { title: "Contract & Cost Management", description: "Optimizing contracts and budgets for cost-effective project delivery.", image: contractManagementImage, icon: <CheckCircle2 className="text-accent" /> },
  { title: "Communications Management", description: "Ensuring seamless communication among stakeholders for project success.", image: communicationManagementImage, icon: <Globe className="text-accent" /> },
  { title: "Human Resources", description: "Managing and optimizing human resources to meet project demands.", image: projecthumanResourcesImage, icon: <CheckCircle2 className="text-accent" /> },
  { title: "Value Engineering (VE)", description: "Delivering innovative solutions to improve project value and efficiency.", image: valueEngineeringImage, icon: <Zap className="text-accent" /> },
  { title: "Hydrology", description: "Advanced water resources management to meet environmental needs.", image: hydrologyImage, icon: <CheckCircle2 className="text-accent" /> },
  { title: "HSE Management", description: "Ensuring workplace safety and compliance with health regulations.", image: onabortionalHealthSafetyImage, icon: <ShieldCheck className="text-accent" /> },
  { title: "Infrastructure", description: "Designing and delivering reliable roads, bridges, and utilities.", image: roadsAndBridgesImage, icon: <CheckCircle2 className="text-accent" /> },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#services") {
      setTimeout(() => {
        const element = document.getElementById("services");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  const scrollToServices = () => {
    const element = document.getElementById("services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Home | Sooners Land LLC Engineering</title>
        <meta name="description" content="Sooners Land LLC provides elite engineering, project management, and infrastructure solutions globally." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center z-0 scale-105"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/40 z-10" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent text-white text-xs font-bold uppercase tracking-widest mb-6">
              Engineering Excellence
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-[1.1]">
              Where Imagination <br />
              <span className="text-accent italic">Meets Engineering.</span>
            </h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl">
              Sooners Land provides elite project management and engineering services for building projects and infrastructure globally, including roads, bridges, and utilities.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToServices}
                className="btn-primary group"
              >
                Explore Services
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="px-6 py-3 rounded-full font-semibold border-2 border-white/20 text-white hover:bg-white hover:text-primary transition-all duration-300"
                onClick={() => window.location.href = '/about'}
              >
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-base font-bold text-accent uppercase tracking-widest mb-3">Our Expertise</h2>
            <h3 className="text-3xl md:text-5xl font-bold mb-6">Comprehensive Engineering Solutions</h3>
            <p className="text-text-muted text-lg">
              We leverage market expertise and a passion for construction to deliver efficient, cost-effective solutions while ensuring quality and safety.
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="glass-card rounded-3xl overflow-hidden group border border-slate-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-8">
                  <div className="mb-4">
                    {service.icon}
                  </div>
                  <h4 className="text-xl font-bold mb-3 group-hover:text-accent transition-colors">
                    {service.title}
                  </h4>
                  <p className="text-text-muted leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-16 text-center">
            <p className="text-sm font-semibold text-slate-400 bg-white inline-block px-8 py-4 rounded-2xl shadow-sm border border-slate-100">
              Read our <a href="/company-profile" className="text-accent hover:underline">Company Profile</a> for more detailed service information.
            </p>
          </div>
        </div>
      </section>

      {/* Short About/Outsource Section */}
      <section className="section-padding bg-primary text-white overflow-hidden relative">
        <div className="absolute inset-0 bg-primary opacity-100 z-0" />
        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <h2 className="text-base font-bold text-white/70 uppercase tracking-widest mb-4 border-l-4 border-accent pl-4">Why Choose Us</h2>
              <h3 className="text-3xl md:text-5xl font-bold mb-8 leading-tight text-white">Delivering Quality through Strategic Partnerships</h3>
              <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                Our services cover all phases of execution and commissioning. We partner with reputable design firms in the USA, Saudi Arabia, and Jordan to ensure the highest standards and innovative solutions.
              </p>
              <ul className="space-y-4">
                {["Efficiency & Cost-effectiveness", "Safety First Approach", "Global Expertise", "End-to-end Project Management"].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-lg">
                    <CheckCircle2 size={20} className="text-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/2 grid grid-cols-2 gap-4">
              <div className="bg-white/5 p-8 rounded-3xl backdrop-blur-sm border border-white/10 text-center">
                <h4 className="text-4xl font-bold text-accent mb-2">15+</h4>
                <p className="text-slate-300 font-semibold uppercase tracking-wider text-xs">Years Experience</p>
              </div>
              <div className="bg-white/5 p-8 rounded-3xl mt-8 backdrop-blur-sm border border-white/10 text-center">
                <h4 className="text-4xl font-bold text-accent mb-2">100%</h4>
                <p className="text-slate-300 font-semibold uppercase tracking-wider text-xs">Project Safety</p>
              </div>
              <div className="bg-white/5 p-8 rounded-3xl -mt-8 backdrop-blur-sm border border-white/10 text-center">
                <h4 className="text-4xl font-bold text-accent mb-2">Global</h4>
                <p className="text-slate-300 font-semibold uppercase tracking-wider text-xs">Presence</p>
              </div>
              <div className="bg-white/5 p-8 rounded-3xl backdrop-blur-sm border border-white/10 text-center">
                <h4 className="text-4xl font-bold text-accent mb-2">Tier-1</h4>
                <p className="text-slate-300 font-semibold uppercase tracking-wider text-xs">Partnerships</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
